import React from 'react';
import { Candidate } from '../types';
import { Briefcase, Code, Languages, GraduationCap, BarChart } from 'lucide-react';

interface AnalysisResultsProps {
  results: Candidate[];
  isLoading: boolean;
  jobDescription: string;
}

export const AnalysisResults: React.FC<AnalysisResultsProps> = ({ 
  results, 
  isLoading,
  jobDescription 
}) => {
  // Add debug logging when the component renders with results
  React.useEffect(() => {
    console.log(`AnalysisResults component rendered with ${results.length} results`);
    if (results.length > 0) {
      console.log('First result:', {
        name: results[0].name,
        matchScore: results[0].matchScore,
        id: results[0].id
      });
    }
  }, [results]); // This will run whenever results change

  if (isLoading) {
    return (
      <div className="animate-pulse p-6">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-6"></div>
        <div className="space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No analysis results available yet.</p>
      </div>
    );
  }

  const ScoreCircle = ({ score, type }: { score: number, type?: 'experience' | 'skills' | 'languages' | 'qualifications' | 'overall' }) => {
    const getScoreColor = () => {
      // First, handle boosted scores (>100) with a special style
      if (score > 100) {
        return 'text-amber-600 border-amber-600 bg-amber-50'; // Gold/amber for boosted scores
      }
      
      // Use specific colors based on the category type
      switch (type) {
        case 'experience':
          return 'text-purple-600 border-purple-600'; // Purple for experience
        case 'skills':
          return 'text-blue-600 border-blue-600'; // Blue for skills
        case 'languages':
          return 'text-indigo-600 border-indigo-600'; // Indigo/purple for languages
        case 'qualifications':
          return 'text-teal-600 border-teal-600'; // Teal for qualifications
        case 'overall':
        default:
          // Default color based on score (for overall)
          if (score >= 85) return 'text-green-600 border-green-600';
          if (score >= 70) return 'text-blue-600 border-blue-600';
          return 'text-gray-600 border-gray-600';
      }
    };

    // Determine if this is a boosted score (from custom rule)
    const isBoosted = score > 100;
    
    // For display, if score > 100, show with a + symbol
    const displayScore = isBoosted ? '100+' : score;

    return (
      <div className="relative">
        <div className={`w-14 h-14 rounded-full border-2 ${getScoreColor()} flex items-center justify-center`}>
          <span className="text-sm font-semibold">{displayScore}{isBoosted ? '' : '%'}</span>
        </div>
        {isBoosted && (
          <div className="absolute -top-2 -right-2 bg-amber-400 text-white text-xs font-bold px-1 rounded-full w-5 h-5 flex items-center justify-center" title="Custom rule bonus applied">
            ★
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="overflow-x-auto shadow-md rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              CANDIDATE
            </th>
            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex flex-col items-center">
                <span>Overall</span>
                <span>Match</span>
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex flex-col items-center">
                <Briefcase className="w-5 h-5 mb-1 text-purple-500" />
                <span>Experience</span>
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex flex-col items-center">
                <Code className="w-5 h-5 mb-1 text-blue-500" />
                <span>Skills</span>
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex flex-col items-center">
                <Languages className="w-5 h-5 mb-1 text-indigo-500" />
                <span>Languages</span>
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="flex flex-col items-center">
                <GraduationCap className="w-5 h-5 mb-1 text-teal-500" />
                <span>Qualifications</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {results.map((candidate, index) => (
            <tr key={candidate.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-gray-500 font-medium">
                      {candidate.name.charAt(0)}
                    </span>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">{candidate.name}</div>
                    <div className="text-sm text-gray-500">{candidate.fileName}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex justify-center">
                  <ScoreCircle score={candidate.matchScore} type="overall" />
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex justify-center">
                  <ScoreCircle score={candidate.experienceScore || 0} type="experience" />
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex justify-center">
                  <ScoreCircle score={candidate.skillsScore || 0} type="skills" />
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex justify-center">
                  <ScoreCircle score={candidate.languageScore || 0} type="languages" />
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex justify-center">
                  <ScoreCircle score={candidate.qualificationsScore || 0} type="qualifications" />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}; 